<template>
  <div class="pr-4">
    <!-- Searching -->
    <p v-if="isSearching" role="status" class="text-muted italic py-4">
      {{ $t("searching") }}
    </p>

    <!-- Search failed -->
    <p v-else-if="errorMessage" role="alert" class="text-red-400 py-4">
      {{ errorMessage }}
    </p>

    <!-- Result -->
    <div v-else-if="data" class="pt-2 pb-40">
      <div class="flex flex-col sm:flex-row gap-6">
        <div
          class="h-72 w-48 flex-none rounded-lg overflow-hidden bg-primary-500 border border-primary-400"
        >
          <img
            v-if="data.poster_path"
            :src="tmdbAsset + data.poster_path"
            class="h-full w-full object-cover"
            :alt="data.name"
          />
        </div>
        <div class="min-w-0">
          <h1 class="text-3xl text-white font-semibold">{{ data.name }}</h1>
          <p class="text-sm mt-2 flex flex-wrap items-center gap-x-3">
            <span
              v-if="data.ratings && data.ratings.length"
              class="text-highlight"
            >
              {{ ratingsText(data.ratings) }}
            </span>
            <span v-if="data.seasons" class="text-subtle">
              {{
                $tc("explore.seasons", data.seasons, { count: data.seasons })
              }}
            </span>
            <span
              v-if="data.seasons"
              :class="data.ongoing ? 'text-highlight' : 'text-subtle'"
            >
              {{ data.ongoing ? $t("explore.ongoing") : $t("explore.ended") }}
            </span>
          </p>

          <!-- Vote -->
          <div class="mt-4 flex items-center gap-1 text-lg">
            <button
              v-for="opt in voteOptions"
              :key="opt.value"
              type="button"
              class="flex h-11 w-11 items-center justify-center rounded-md transition"
              :class="
                searchVote === opt.value
                  ? 'text-highlight'
                  : 'text-muted opacity-75 hover:opacity-100'
              "
              :aria-pressed="searchVote === opt.value ? 'true' : 'false'"
              :aria-label="$t('vote.' + opt.value)"
              :title="$t('vote.' + opt.value)"
              @click.prevent="setSearchVote(opt.value)"
            >
              <Font-awesome-icon
                :icon="[searchVote === opt.value ? 'fas' : 'far', opt.icon]"
              />
            </button>
          </div>

          <p class="mt-4 text-subtle text-sm leading-relaxed max-w-2xl">
            {{ data.overview }}
          </p>

          <!-- Track / tracked state -->
          <div class="mt-8">
            <template v-if="!isTracking">
              <Button
                :loading="isTrackingLoad"
                :disabled="!canTrackMore"
                class="uppercase tracking-widest"
                @click.prevent="trackSeries(data.id)"
              >
                {{ $t("explore.track") }}
              </Button>
              <!-- Explain the limit up front rather than only on failure. -->
              <p v-if="!canTrackMore" class="mt-3 text-subtle text-sm">
                {{ $t("account.freeLimitReached", { count: seriesLimit }) }}
                <router-link
                  to="/settings"
                  class="text-highlight hover:underline"
                >
                  {{ $t("account.upgradeToPro") }}
                </router-link>
              </p>
            </template>
            <div v-else class="flex items-center gap-4">
              <p class="text-highlight" role="status">
                {{ $t("explore.nowTracking", { name: data.name }) }}
              </p>
              <Button
                class="uppercase tracking-widest"
                @click.prevent="clearSearch"
              >
                {{ $t("explore.ok") }}
              </Button>
            </div>
            <!-- Track failures show here, next to the action that failed, so
                 the series the user was looking at stays on screen. -->
            <ErrorLabel :error="trackError" />
          </div>
        </div>
      </div>
    </div>

    <!-- Searched, but nothing matched -->
    <p v-else-if="query" class="text-muted py-4">
      {{ $t("explore.noResults", { query }) }}
    </p>

    <!-- No active query: hint + genre filter + recommendations -->
    <section v-else>
      <h1 class="text-2xl uppercase font-semibold text-white">
        {{ $t("explore.recommended") }}
      </h1>
      <p class="text-subtle uppercase text-xs tracking-widest mt-2">
        {{ $t("explore.recommendedSub") }}
      </p>

      <!-- Genre filter -->
      <div
        class="mt-5 flex flex-wrap gap-2"
        role="group"
        :aria-label="$t('explore.filterGenre')"
      >
        <button
          type="button"
          class="px-4 py-2.5 rounded-full text-sm transition"
          :class="chipClass(null)"
          :aria-pressed="selectedGenre === null ? 'true' : 'false'"
          @click="selectGenre(null)"
        >
          {{ $t("explore.allGenres") }}
        </button>
        <button
          v-for="g in genres"
          :key="g.id"
          type="button"
          class="px-4 py-2.5 rounded-full text-sm transition"
          :class="chipClass(g.id)"
          :aria-pressed="selectedGenre === g.id ? 'true' : 'false'"
          @click="selectGenre(g.id)"
        >
          {{ g.name }}
        </button>
      </div>

      <!-- Status filter (ongoing / ended) -->
      <div
        class="mt-3 flex flex-wrap gap-2"
        role="group"
        :aria-label="$t('explore.filterStatus')"
      >
        <button
          v-for="opt in statusOptions"
          :key="opt.value ?? 'all'"
          type="button"
          class="px-4 py-2.5 rounded-full text-sm transition"
          :class="statusChipClass(opt.value)"
          :aria-pressed="selectedStatus === opt.value ? 'true' : 'false'"
          @click="selectStatus(opt.value)"
        >
          {{ $t(opt.label) }}
        </button>
      </div>

      <!-- Sections -->
      <div v-if="filteredRecommendations.length" class="mt-8 space-y-10">
        <section v-for="sec in filteredRecommendations" :key="sec.genre.id">
          <h2 class="text-lg uppercase font-semibold text-white mb-4">
            {{ sec.genre.name }}
          </h2>
          <div class="flex gap-6 overflow-x-auto pb-2">
            <RecommendationCard
              v-for="s in sec.series"
              :key="s.id"
              :series="s"
            />
          </div>
        </section>
      </div>
      <p v-else-if="recsError" role="alert" class="text-red-400 text-sm mt-8">
        {{ recsError }}
      </p>
      <p v-else-if="recommendations.length" class="text-muted text-sm mt-8">
        {{ $t("explore.noneForStatus") }}
      </p>
      <!-- Only claim to be loading while a request is actually in flight —
           a failed fetch used to leave "Loading.." on screen forever. -->
      <p v-else-if="recsLoading" role="status" class="text-muted text-sm mt-8">
        {{ $t("loading") }}
      </p>
      <p v-else class="text-muted text-sm mt-8">
        {{ $t("explore.noneRecommended") }}
      </p>
    </section>
  </div>
</template>

<script>
import Button from "@/components/common/Button.vue";
import ErrorLabel from "@/components/common/ErrorLabel.vue";
import RecommendationCard from "@/components/series/RecommendationCard.vue";
import { isPlanLimitError } from "@/utils/errors";

export default {
  name: "Explore",
  components: { Button, ErrorLabel, RecommendationCard },

  data() {
    return {
      data: null,
      isSearching: false,
      isTracking: false,
      isTrackingLoad: false,
      errorMessage: "",
      trackError: "",
      recsLoading: false,
      recsError: "",
      selectedGenre: null,
      selectedStatus: null,
      statusOptions: [
        { value: null, label: "explore.allStatus" },
        { value: "ongoing", label: "explore.ongoing" },
        { value: "ended", label: "explore.ended" },
      ],
      searchVote: null,
      voteOptions: [
        { value: "dislike", icon: "thumbs-down" },
        { value: "like", icon: "thumbs-up" },
        { value: "really_like", icon: "heart" },
      ],
    };
  },

  computed: {
    tmdbAsset() {
      return process.env.VUE_APP_TMDB_ASSET ?? "";
    },
    query() {
      return this.$route.query.q ?? "";
    },
    recommendations() {
      return this.$store.getters.recommendations;
    },
    canTrackMore() {
      return this.$store.getters.canTrackMore;
    },
    seriesLimit() {
      return this.$store.getters.seriesLimit;
    },
    genres() {
      return this.$store.getters.genres;
    },
    /** Recommendation sections with each series list filtered by the
     *  ongoing/ended status; sections left empty are dropped. */
    filteredRecommendations() {
      if (this.selectedStatus === null) return this.recommendations;
      const wantOngoing = this.selectedStatus === "ongoing";
      return this.recommendations
        .map((sec) => ({
          ...sec,
          series: sec.series.filter((s) => Boolean(s.ongoing) === wantOngoing),
        }))
        .filter((sec) => sec.series.length);
    },
  },

  watch: {
    // React to searches submitted from the top bar while already on this page.
    query(q) {
      if (q) this.runSearch(q);
      else this.clearSearch();
    },
  },

  mounted() {
    if (this.query) this.runSearch(this.query);
    if (!this.genres.length) {
      this.$store.dispatch("fetchGenres").catch(() => undefined);
    }
    if (!this.recommendations.length) {
      this.loadRecommendations();
    }
  },

  methods: {
    /** Fetch recommendations, tracking in-flight and failed states so the
     *  view can say which one it is. */
    loadRecommendations(genreId) {
      this.recsLoading = true;
      this.recsError = "";
      return this.$store
        .dispatch("fetchRecommendations", genreId)
        .catch(() => (this.recsError = this.$t("explore.error").toString()))
        .finally(() => (this.recsLoading = false));
    },
    chipClass(id) {
      return this.selectedGenre === id
        ? "bg-highlight text-primary-700 font-semibold"
        : "bg-primary-500 text-muted hover:text-highlight";
    },
    selectGenre(id) {
      if (this.selectedGenre === id) return;
      this.selectedGenre = id;
      this.$store.commit("setRecommendations", []);
      this.loadRecommendations(id);
    },
    statusChipClass(value) {
      return this.selectedStatus === value
        ? "bg-highlight text-primary-700 font-semibold"
        : "bg-primary-500 text-muted hover:text-highlight";
    },
    selectStatus(value) {
      // Client-side filter — no refetch needed.
      this.selectedStatus = value;
    },
    ratingsText(ratings) {
      return (ratings || [])
        .map(
          (r) =>
            `${r.source === "Rotten Tomatoes" ? "RT" : r.source} ${r.value}`,
        )
        .join("   ·   ");
    },
    setSearchVote(value) {
      const next = this.searchVote === value ? null : value;
      this.searchVote = next;
      this.$store
        .dispatch("vote", { seriesId: this.data.id, vote: next })
        .catch(() => undefined);
    },
    runSearch(query) {
      this.isSearching = true;
      this.errorMessage = "";
      this.trackError = "";
      this.isTracking = false;
      this.data = null;

      this.$store
        .dispatch("search", query)
        .then((response) => {
          // Treat an empty body / empty object (no match) as "no results".
          const d = response.data;
          this.data = d && Object.keys(d).length ? d : null;
          this.searchVote = this.data ? (this.data.vote ?? null) : null;
        })
        .catch(() => {
          this.errorMessage = this.$t("explore.error").toString();
        })
        .finally(() => (this.isSearching = false));
    },

    trackSeries(id) {
      this.isTrackingLoad = true;
      this.trackError = "";

      this.$store
        .dispatch("track", id)
        .then(() => (this.isTracking = true))
        .catch((error) => {
          this.trackError = isPlanLimitError(error)
            ? this.$t("account.freeLimitReached", {
                count: this.seriesLimit,
              }).toString()
            : this.$t("explore.error").toString();
        })
        .finally(() => (this.isTrackingLoad = false));
    },

    clearSearch() {
      this.data = null;
      this.isTracking = false;
      this.errorMessage = "";
      this.trackError = "";
    },
  },
};
</script>
